import * as React from "react";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGlobalAuthContext } from "../context/AuthContext";
import { ReactComponent as LogoIcon } from "../Assets/mf-logo.svg";
import { LoadingButton } from "@mui/lab";
import {
  useRequestNewPasswordMutation,
  useResetPasswordMutation,
} from "../Slices/roomNateApis";
import EmailOtpConfirmation from "../Components/EmailOtpConfirmation";
import AuthPageLayout from "../Components/Auth/AuthPageLayout";
import AuthPasswordField from "../Components/Auth/AuthPasswordField";
import AuthSwitchLink from "../Components/Auth/AuthSwitchLink";
import {
  getAuthGlassFieldSx,
  getAuthPrimaryButtonSx,
} from "../Components/Auth/authGlassStyles";

export default function ForgetPswPage() {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const theme = useTheme();
  const [dilogueOpen, setDialogOpen] = React.useState(false);
  const [otpConfimed, setOtpConfimed] = React.useState(null);
  const [showPsw, setShowPsw] = React.useState(false);
  const [showConfirmPsw, setShowConfirmPsw] = React.useState(false);

  const navigate = useNavigate();
  const [requestNewPassword] = useRequestNewPasswordMutation();
  const [resetPassword] = useResetPasswordMutation();

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Enter your email")
        .matches(
          "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$",
          "Enter a valid email"
        ),
    }),
    onSubmit: (values) => {
      requestNewPassword({
        body: values,
      });
      setDialogOpen(true);
    },
  });

  const formikNewPassword = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Choose a password"),
      confirmPassword: Yup.string()
        .required("Confirm your password")
        .oneOf([Yup.ref("password"), null], "Passwords must match"),
    }),
    onSubmit: (values) => {
      resetPassword({
        body: {
          email: formik?.values?.email,
          validationCode: otpConfimed,
          password: values?.password,
        },
      });
      navigate("/login");
    },
  });

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <AuthPageLayout
      footer={
        !currentUserSessionStorage ? (
          <Box sx={{ mt: 2.5, textAlign: "center" }}>
            <AuthSwitchLink
              mutedText="Remember your password?"
              linkText="Log in"
              to="/login"
              sx={{ mt: 0 }}
            />
          </Box>
        ) : null
      }
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 2.5 }}>
        <LogoIcon width={52} height={52} />
      </Box>

      <Typography
        component="h1"
        sx={{
          fontSize: { xs: "21px", sm: "22px" },
          fontWeight: 600,
          color: "text.primary",
          letterSpacing: "-0.02em",
          mb: !otpConfimed ? 0.75 : 2.5,
          textAlign: "center",
          lineHeight: 1.25,
          px: 0.5,
        }}
      >
        {!otpConfimed ? "Forgot your password?" : "Set new password"}
      </Typography>

      {!otpConfimed && (
        <Typography
          sx={{
            fontSize: "14px",
            color: "rgba(0,0,0,0.55)",
            textAlign: "center",
            lineHeight: 1.4,
            mb: 2.5,
            px: 0.5,
          }}
        >
          We'll email you a link to reset it.
        </Typography>
      )}

      {!otpConfimed ? (
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          noValidate
          key="resetPassword"
        >
          <Stack spacing={1.75}>
            <TextField
              variant="outlined"
              label={
                formik.touched.email && formik.errors.email
                  ? formik.errors.email
                  : "Email"
              }
              required
              fullWidth
              id="email"
              name="email"
              type="email"
              InputProps={{ style: { height: 48 } }}
              inputProps={{
                autoComplete: "email",
                inputMode: "email",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                enterKeyHint: "send",
              }}
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              sx={getAuthGlassFieldSx(theme)}
            />

            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              disableElevation
              sx={{ ...getAuthPrimaryButtonSx(theme), mt: 0.5 }}
            >
              Send reset link
            </LoadingButton>
          </Stack>
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={formikNewPassword.handleSubmit}
          noValidate
          key="newPassword"
        >
          <Stack spacing={1.75}>
            <AuthPasswordField
              fullWidth
              id="password"
              name="password"
              label={
                formikNewPassword.touched.password &&
                formikNewPassword.errors.password
                  ? formikNewPassword.errors.password
                  : "New password"
              }
              inputProps={{
                autoComplete: "new-password",
                enterKeyHint: "next",
                autoCorrect: "off",
                autoCapitalize: "none",
                spellCheck: false,
              }}
              value={formikNewPassword.values.password}
              onChange={formikNewPassword.handleChange}
              onBlur={formikNewPassword.handleBlur}
              error={
                formikNewPassword.touched.password &&
                Boolean(formikNewPassword.errors.password)
              }
              showPsw={showPsw}
              onToggle={() => setShowPsw((v) => !v)}
            />

            <AuthPasswordField
              fullWidth
              id="confirmPassword"
              name="confirmPassword"
              label={
                formikNewPassword.touched.confirmPassword &&
                formikNewPassword.errors.confirmPassword
                  ? formikNewPassword.errors.confirmPassword
                  : "Confirm password"
              }
              inputProps={{
                autoComplete: "new-password",
                enterKeyHint: "done",
                autoCorrect: "off",
                autoCapitalize: "none",
                spellCheck: false,
              }}
              value={formikNewPassword.values.confirmPassword}
              onChange={formikNewPassword.handleChange}
              onBlur={formikNewPassword.handleBlur}
              error={
                formikNewPassword.touched.confirmPassword &&
                Boolean(formikNewPassword.errors.confirmPassword)
              }
              showPsw={showConfirmPsw}
              onToggle={() => setShowConfirmPsw((v) => !v)}
            />

            <LoadingButton
              variant="contained"
              fullWidth
              type="submit"
              disableElevation
              sx={{ ...getAuthPrimaryButtonSx(theme), mt: 0.5 }}
            >
              Set new password
            </LoadingButton>
          </Stack>
        </Box>
      )}

      <EmailOtpConfirmation
        dialogOpen={dilogueOpen}
        setDialogOpen={setDialogOpen}
        sendTo={formik?.values?.email}
        title="Reset Password"
        setOtpConfimed={setOtpConfimed}
        afterConfirmation={() => {
          setDialogOpen(false);
          formikNewPassword.resetForm();
        }}
      />
    </AuthPageLayout>
  );
}
